import { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

export type TourStep = {
  selector?: string;       // CSS selector. Omit for a centered modal step.
  title: string;
  body: string;
  placement?: "top" | "bottom" | "left" | "right" | "auto";
};

const STORAGE_KEY = "cario-onboarding-v1";
const EVENT_START = "cario:start-tour";

export function startOnboardingTour() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(EVENT_START));
}

export function OnboardingTour({ steps, autoStart = true }: { steps: TourStep[]; autoStart?: boolean }) {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  // Auto-start on first ever visit
  useEffect(() => {
    if (!autoStart || typeof window === "undefined") return;
    const seen = window.localStorage.getItem(STORAGE_KEY);
    if (!seen) {
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [autoStart]);

  // Listen for manual triggers
  useEffect(() => {
    const onStart = () => { setIdx(0); setOpen(true); };
    window.addEventListener(EVENT_START, onStart);
    return () => window.removeEventListener(EVENT_START, onStart);
  }, []);

  const step = steps[idx];

  // Track target element position.
  // NOTE: no polling here. A repeating timer that called scrollIntoView +
  // setState with a fresh DOMRect made the whole page re-render (and fight the
  // smooth scroll) several times a second, which felt like a frozen dashboard.
  useLayoutEffect(() => {
    if (!open || !step?.selector) { setRect(null); return; }
    const target = document.querySelector(step.selector);
    if (!target) { setRect(null); return; }

    // Scroll to the step target exactly once, when the step becomes active.
    target.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });

    let frame = 0;
    const measure = () => {
      frame = 0;
      const next = target.getBoundingClientRect();
      setRect((prev) =>
        prev &&
        prev.top === next.top &&
        prev.left === next.left &&
        prev.width === next.width &&
        prev.height === next.height
          ? prev
          : next,
      );
    };
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(measure);
    };
    measure();
    const ro = new ResizeObserver(schedule);
    ro.observe(target);
    window.addEventListener("resize", schedule, { passive: true });
    window.addEventListener("scroll", schedule, { passive: true, capture: true });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      ro.disconnect();
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, true);
    };
  }, [open, step?.selector, idx]);


  if (!mounted || !open || !step) return null;

  const finish = () => {
    setOpen(false);
    setIdx(0);
    try { window.localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
  };
  const next = () => { if (idx < steps.length - 1) setIdx(idx + 1); else finish(); };
  const prev = () => { if (idx > 0) setIdx(idx - 1); };

  const pad = 8;
  const tipW = 320;
  const tipH = 180;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;

  // Compute tooltip position
  let tipStyle: React.CSSProperties;
  let spotlight: React.CSSProperties | null = null;
  if (rect) {
    spotlight = {
      position: "fixed",
      top: rect.top - pad,
      left: rect.left - pad,
      width: rect.width + pad * 2,
      height: rect.height + pad * 2,
      borderRadius: 12,
      boxShadow: "0 0 0 9999px rgba(2,6,23,0.78), 0 0 0 2px var(--cyan, #22d3ee), 0 0 30px 4px rgba(34,211,238,0.45)",
      pointerEvents: "none",
      zIndex: 9998,
      transition: "all 180ms ease",
    };
    // Decide placement
    const below = rect.bottom + pad + 12;
    const above = rect.top - pad - 12 - tipH;
    const placeBelow = below + tipH < vh - 12 || above < 12;
    const top = placeBelow ? below : Math.max(12, above);
    let left = rect.left + rect.width / 2 - tipW / 2;
    left = Math.max(12, Math.min(left, vw - tipW - 12));
    tipStyle = { position: "fixed", top, left, width: tipW, zIndex: 9999 };
  } else {
    tipStyle = {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: Math.min(tipW, vw - 24),
      zIndex: 9999,
    };
  }

  return createPortal(
    <>
      {!rect && (
        <div
          className="fixed inset-0 z-[9997] bg-background/80 backdrop-blur-sm"
          onClick={finish}
        />
      )}
      {spotlight && <div style={spotlight} />}
      <div style={tipStyle} className="glass rounded-xl border border-panel-border bg-panel/95 p-4 shadow-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">
            <Sparkles className="h-3 w-3" />
            Tour · {idx + 1}/{steps.length}
          </div>
          <button
            aria-label="Close tour"
            onClick={finish}
            className="rounded-md p-1 text-muted-foreground hover:bg-white/5 hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="mt-2 font-display text-lg leading-tight">{step.title}</div>
        <div className="mt-1 text-sm text-muted-foreground leading-relaxed">{step.body}</div>
        <div className="mt-3 flex items-center gap-1.5">
          <div className="flex gap-1">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 w-1.5 rounded-full ${i === idx ? "bg-cyan" : "bg-panel-border"}`}
              />
            ))}
          </div>
          <button
            onClick={finish}
            className="ml-auto rounded-md px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
          >
            Skip
          </button>
          {idx > 0 && (
            <button
              onClick={prev}
              className="inline-flex items-center gap-1 rounded-md border border-panel-border bg-background/40 px-2.5 py-1 text-xs hover:bg-panel"
            >
              <ArrowLeft className="h-3 w-3" /> Back
            </button>
          )}
          <button
            onClick={next}
            className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:opacity-90"
          >
            {idx === steps.length - 1 ? "Done" : "Next"} <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}